import { DecisionGraph, graphToDecisionGraph, isDecisionGraph } from "@imminently/decisively-core";
import { useMemo } from "react";
import { useFullRelease } from "./hooks_useFullRelease";

type GraphResult = { decisionGraph?: any; graph?: any } | null | undefined;

const builtGraphs = new WeakMap<object, WeakMap<object, DecisionGraph>>();
const NO_RELEASE = {};

/**
 * A working graph (a DecisionGraph, or legacy JSON) as a DecisionGraph with the release's rule data, made once per graph
 * object and release. graphToDecisionGraph re-encodes an edited DecisionGraph on every call, and each mounted view
 * (attribute panels, focus nodes) would otherwise pay for that. Graphs handed to views aren't edited in place.
 */
export const cachedDecisionGraphOf = (graph: object, release?: any): DecisionGraph => {
  const releaseKey = (release ?? NO_RELEASE) as object;
  let byRelease = builtGraphs.get(graph);
  let decisionGraph = byRelease?.get(releaseKey);
  if (decisionGraph) return decisionGraph;
  decisionGraph = graphToDecisionGraph(graph as any, { release });
  if (!byRelease) {
    byRelease = new WeakMap();
    builtGraphs.set(graph, byRelease);
  }
  byRelease.set(releaseKey, decisionGraph);
  return decisionGraph;
};

/**
 * The decision graph a result shows (a decisionapi response, or a saved session or report), with the release's rule
 * data: its decision graph, or for older results its legacy `graph` converted to one.
 */
export const resultDecisionGraph = (result: GraphResult, release?: any): DecisionGraph | undefined => {
  if (!result) return undefined;
  if (result.decisionGraph !== undefined && result.decisionGraph !== null) {
    return DecisionGraph.from(result.decisionGraph, { release: release ?? undefined });
  }
  if (!result.graph) return undefined;
  return cachedDecisionGraphOf(DecisionGraph.from(result.graph), release);
};

/** The decision graph to show for a result (made once per result and release). */
export const useResultDecisionGraph = (result: GraphResult): DecisionGraph | undefined => {
  const release = useFullRelease();
  return useMemo(() => resultDecisionGraph(result, release), [result?.decisionGraph, result?.graph, release]);
};

/**
 * A view's graph as a DecisionGraph: a DecisionGraph as it is; legacy JSON (or a graphlib graph) from code that still
 * has one (sessions, saved reports, the release's rule graph) converted once per graph and release (shared across mounts), with the release's rule data.
 */
export const useDecisionGraphOf = (graph: unknown): DecisionGraph | undefined => {
  const release = useFullRelease();
  return useMemo(() => {
    if (!graph) return undefined;
    if (isDecisionGraph(graph)) return graph;
    return cachedDecisionGraphOf(DecisionGraph.from(graph as any), release);
  }, [graph, release]);
};
